import { ArrowUp } from 'lucide-react';
import { useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { useT } from '@/lib/i18n';
import type { scrollToTop } from '@/motion/scroll/to-top';
import { useScrolledPast } from './useScrolledPast';

interface ToTopModule {
  scrollToTop: typeof scrollToTop;
}

let loading: Promise<ToTopModule> | undefined;

/**
 * The scroll helper is first-use code (about 0.7 kB gzip the first page does not need: the budgets are tight).
 * It is fetched as soon as the arrow is on screen, so by the time it is pressed the module is in memory; a failed
 * fetch (offline, a stale chunk) is forgotten so the next press tries again.
 */
function loadHelper(): Promise<ToTopModule> {
  loading ??= import('@/motion/scroll/to-top').catch((error: unknown) => {
    loading = undefined;
    throw error;
  });
  return loading;
}

/** Back-to-top: 44 px round disc, bottom-right, safe-area aware; appears after ~1.2 screens. */
export function ToTop() {
  const t = useT();
  const visible = useScrolledPast(1.2);

  useEffect(() => {
    if (visible) loadHelper().catch(() => undefined);
  }, [visible]);

  if (!visible) return null;

  const onClick = () => {
    // Focus first (the button is gone once the page is near the top): <main> is the start of the page.
    document.getElementById('main')?.focus({ preventScroll: true });
    // Our own frame-by-frame scroll, not `behavior: 'smooth'`: the browser's smooth scroll loses to Lenis's
    // glide and to layout shifts, which made the arrow "sometimes" do nothing (D-356). If the helper cannot be
    // loaded the arrow still works the old way.
    loadHelper().then(
      ({ scrollToTop }) => {
        scrollToTop();
      },
      () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
    );
  };

  return (
    <Button variant="icon" className="to-top" aria-label={t('nav.backToTop')} onClick={onClick}>
      <ArrowUp size={22} aria-hidden="true" />
    </Button>
  );
}
